/* eslint-disable react/jsx-closing-bracket-location */
import { Box } from '@material-ui/core';
import { Row } from 'components/layout/Row';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Column } from 'components/layout/Column';
import format from 'date-fns/format';
import { parseISO } from 'date-fns';
import { DEFAULT_GROUND_COLOR, PLAYER_MARKER_COLOR } from 'components/track/ground/constants';
import { CLUB, ClubTypeRes, ClubTypeVars } from 'query/club';
import { getMarkersColor, getPairedGroundColor, GroundType } from 'models/ground';
import { TourProvider } from '@reactour/tour';
import { FPS } from 'components/constants';
import StaffOnlyWrapper from 'components/StaffOnlyWrapper';
import { useLazyQueryCached, useQueryCached } from '../utils/graphql';
import { TeamSessionsType } from '../../models/team_session';
import { SESSION, TEAMSESSION_ATHLETES_SESSIONS, TeamSessionsVars, TeamSessionType, } from '../../query/session';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import { CursorContext } from '../track/TrackChartContainer';
import PlayerControls from './PlayerControls';
import PlayersTable from './PlayersTable';
import ErrorGuard from '../layout/ErrorGuard';
import WebPlayerHeader from './WebPlayerHeader';
import Ground from './Ground';
import {
  enterFullscreen,
  exitFullscreen,
  filterNeighbors,
  findNearTimestamp,
  findNearTimestampIndex,
  FpsCtrl,
  getCurrentDrill,
} from './utils';
import {
  DrillTimesType,
  InfoPlayers,
  MarkDrill,
  PlayerDataInterval,
  PlayerDataIntervals,
  PlayersDetails,
  PlayersExtraData,
  PlayersTeams,
  PlayersTeamsContextValue,
  SeriesState,
  STATUS_TEXT,
  WebPlayerProps,
} from '../../types/webPlayer';
import { OptionType } from '../Autocomplete';
import TourGuide, { steps } from './TourGuide';
import { withMe } from '../MeUserWrapper';
import { MeType } from '../../models/me';
import { AthleteSessionType } from '../../models/athlete_session';
import { loadData, loadSessionData, packData } from '../../lib/data-loader';
import { getNextSlot, getSlotId, getSlotStartTimestamp } from '../../lib/data-loader/utils';
import { SessionDataManager } from '../../lib/data-loader/session-data-manager';
import client_settings from '../../lib/client_settings';
import { useTranslation } from 'react-i18next';
import { URL_REGEXP_ATHID, urlRegExp } from '../../lib/data-loader/constants';
import { Loader } from '../../lib/icons';
import InlineMessage from '../InlineMessage';

// @todo a volte resta in loop di caricamento dei file BINARI, verificare
// @todo verificare se rimuovere gli useMemo per ridurre uso RAM


const PLAYER_STATUS = {
  STOP: false,
  PLAY: true,
};

const LOADING_TYPE = {
  NONE: 0,
  BG: 1,
  FG: 2,
} as const;

type LoadingType = typeof LOADING_TYPE[keyof typeof LOADING_TYPE];

const PLAYER_DATA_STATUS = {
  INITIAL: 0,
  TIMESTAMPS_LOADING: 10,
  TIMESTAMPS_LOADED: 20,
  DATA_LOADING: 30,
  DATA_LOADED: 40,
  DATA_DECODING: 50,
  DATA_DECODED: 60,
  PLAYER_INITIALIZING: 80,
  PLAYER_INITED: 90,
  READY: 100,
  ERROR: 500,
} as const;

type PlayerDataStatus = typeof PLAYER_DATA_STATUS[keyof typeof PLAYER_DATA_STATUS];

const timeFormat = 'H:mm:ss';
const valueLabelFormat = ( value: number, _: number ) => format(value, timeFormat);

export const PlayersTeamsContext = React.createContext<PlayersTeamsContextValue>({
  playersTeams: {asd: ['asdasd']},
  setPlayersTeams: () => {
    console.log('default');
  },
});

const keyboardEventHandler = (
  event: KeyboardEvent,
  keyCallbacks: {
    key: string,
    callbackFn: Function,
    ctrlKey?: boolean,
    altKey?: boolean,
    shiftKey?: boolean,
    metaKey?: boolean
  }[],
) => {
  const callback = keyCallbacks.find(( k ) => k.key === event.code
    && !!k.ctrlKey === event.ctrlKey
    && !!k.altKey === event.altKey
    && !!k.metaKey === event.metaKey
    && !!k.shiftKey === event.shiftKey);

  if (callback) {
    callback.callbackFn();
  }
};

const dbManager = new SessionDataManager({dbName: 'session-data-db', version: 1});

const isSessionDataAvailable = async ( sessionId: string ) => {
  await dbManager.open();

  const range = IDBKeyRange.bound([sessionId], [sessionId, []]);
  const out = await dbManager.db.exists(range);

  return out;
}

const getSessionTimestamps = async ( sessionId: string ) => {
  try {
    const response = await fetch(
      `${client_settings.public.gpexeBackendUrl}rest/v2/session/team/${sessionId}/get_timestamps`, // @todo usare le env
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          authorization: `JWT ${localStorage.getItem('exelio_token')}`,
        },
      }
    );

    return await response.json();
  } catch (e: Error | any) {
    console.log('Errore getSessionTimestamps', e)
  }
}

const delay = ( durationMs ) => {
  return new Promise(resolve => setTimeout(resolve, durationMs));
}

const WebPlayer = ( props: WebPlayerProps & { me: MeType } ) => {
  const {
    currentDrill,
    defaultGround,
    me,
    isFullscreen,
    sessionGround,
    sessionId,
    showVertices,
    templateId,
    teamId,
  } = props;

  const {t} = useTranslation();

  const [currentActivePlayer, setCurrentActivePlayer] = useState<null | string>(null);
  const [currentHoverPlayer, setCurrentHoverPlayer] = useState<null | string>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [enabledPlayers, setEnabledPlayers] = useState<Array<string>>([]);
  const [ground, setGround] = useState<GroundType | undefined>(sessionGround);
  const [intervals, setIntervals] = useState<PlayerDataIntervals>([]);
  const [loadingInterval, setLoadingInterval] = useState<PlayerDataInterval | null>(null);
  const [jerseyOrNumber, setJerseyOrNumber] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState<Array<string>>([]);
  const [marks, setMarks] = useState<MarkDrill[]>([]);
  const [playerSpeed, setPlayerSpeed] = useState(1); // ms tra i frame (1/25)
  const [playersColors, setPlayersColors] = useState<Record<string, string>>({});
  const [series, setSeries] = useState<SeriesState>({});
  const [status, setStatus] = useState(STATUS_TEXT.INIT);
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [timestampsToAdd, setTimestampsToAdd] = useState<number[]>([]);
  const [trailsEnabled, setTrailsEnabled] = useState(false);
  const [currentPlayerStatus, setCurrentPlayerStatus] = useState(false);
  const [infoPlayers, setInfoPlayers] = useState<InfoPlayers>([]);
  const [playersTeams, setPlayersTeams] = useState<PlayersTeams>({});
  const [playerLinks, setPlayerLinks] = useState<Set<[string, string]>>(new Set());
  const [playerFPS, setPlayerFPS] = useState(FPS);
  const [dataState, setDataState] = useState<any>(null);
  const [debug, setDebug] = useState(false);
  const [isDecodingData, setIsDecodingData] = useState(false);
  const [timestampsLoading, setTimestampsLoading] = useState(false);
  const [playerIsLoading, setPlayerIsLoading] = useState(true);
  const [playerIsFullscreen, setPlayerIsFullscreen] = useState(false);

  const playerRef = React.useRef<null | HTMLDivElement>(null);
  const currDrillRef = useRef(1);
  const currPlayerSpeedRef = useRef(1);
  const fpsCtrlRef = useRef<any>();
  const lastRequestedTimestampRef = useRef<number | null>(null);
  const loadingTypeRef = useRef<LoadingType>(LOADING_TYPE.NONE);
  const loadingPlayersRef = useRef<number>(0);

  const isLoadingForeground = loadingTypeRef.current === LOADING_TYPE.FG;

  const currentTimeSlot = currentTime && getSlotId(currentTime) || 0;

  const logger = useCallback(( ...content ) => {
    if (debug) {
      console.log('[LOGGER]', content);
    }
  }, [debug])

  const handleCurrentTimeChange = useCallback(( value: number ) => {
    lastRequestedTimestampRef.current = value;
    setCurrentTime(value);
  }, []);

  const cursorValue = useMemo(() => ({
    cursor: findNearTimestamp(currentTime, timestamps),
    setCursor: handleCurrentTimeChange,
  }), [currentTime, findNearTimestamp, handleCurrentTimeChange, timestamps]);

  const loadTimestamps = useCallback(async () => {
    setTimestampsLoading(true);
    const tss = await getSessionTimestamps(sessionId);
    logger('timestamps', tss)
    setTimestamps(tss.timestamps.map(( t ) => t * 1000));
    setTimestampsLoading(false);
  }, [sessionId])

  /* SESSION DATA */
  const {
    loading: sessionLoading,
    data,
  } = useQueryCached<{ res: TeamSessionType }, TeamSessionsVars>(SESSION, {
    variables: {
      drill: currentDrill,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
      withAthleteSession: true,
      withGround: true,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: async ( result ) => {
      const tracksGround = result?.res?.athleteSessions && result.res.athleteSessions.length > 0
        && result.res.athleteSessions[0] && result.res.athleteSessions[0].track?.ground;

      if (result?.res?.startTimestamp) {
        const timezoneOffset = -new Date(result.res.startTimestamp).getTimezoneOffset() * 60000;
        const newTimestamp = new Date(result.res.startTimestamp).getTime() + timezoneOffset;
        setCurrentTime(newTimestamp);
        lastRequestedTimestampRef.current = newTimestamp;
        setStatus(STATUS_TEXT.SET_START_TIME);
      }

      logger('tracksGround', tracksGround);

      if (result?.res?.drills?.relatedDrills) {
        const drillsMarks: {
          value: number,
          label: string,
          index?: number,
          id?: number,
          tags?: string[],
        }[] = [];
        setStatus(STATUS_TEXT.DATA_SESSION_LOADED);

        result.res.drills.relatedDrills.forEach(( drill ) => {
          const timezoneOffset = drill?.start
            ? -new Date(drill.start).getTimezoneOffset() * 60000
            : 0;

          drillsMarks.push({
            value: drill ? (new Date(drill.start)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.start), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });

          // @todo fare in modo che il valore sia con indice e non timestamp
          drillsMarks.push({
            value: drill ? (new Date(drill.end)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.end), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });
        });

        setMarks(drillsMarks);
        setStatus(STATUS_TEXT.SET_DRILLS);
      }

      if (tracksGround) {
        setGround(tracksGround);
      }

      loadTimestamps()

      setStatus(STATUS_TEXT.PLAYERS_LOADING);
      // eslint-disable-next-line no-use-before-define
      loadSessions();
    },
  });

  const endSessionTimestampRef = useRef<number | null>(0);

  const endSessionTimestamp = useMemo(() => {
    if (data?.res?.endTimestamp) {
      return new Date(data.res.endTimestamp).getTime() - new Date(data.res.endTimestamp).getTimezoneOffset() * 60000;
    }

    return 0;
  }, [data?.res?.endTimestamp]);

  useEffect(() => {
    endSessionTimestampRef.current = endSessionTimestamp;
  }, [endSessionTimestamp]);

  /* SESSION DATA - DETAILS */
  // @todo usare webworker per non bloccare il main thread
  const [loadSessions, {
    data: detailsData,
  }] = useLazyQueryCached<{ res: TeamSessionsType }, TeamSessionsVars>(TEAMSESSION_ATHLETES_SESSIONS, {
    variables: {
      drill: currentDrill,
      fieldsLimit: 6,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
    },
    fetchPolicy: 'cache-first',
    onCompleted: ( d ) => {
      setStatus(STATUS_TEXT.PLAYERS_READY);
      setEnabledPlayers(d.res.athleteSessions.map(( a ) => a.id));
    },
    onError: ( error ) => {
      console.error('Error fetching athletes', error);
    },
  });

  /* ATH SESSIONS */
  const athSess = detailsData?.res.athleteSessions || [];
  const athSessionsNumRef = useRef(athSess.length);
  // const maxSamples = getLoadingTimeFrame(athSessionsNumRef.current) * maxSamplesPerFrame;

  useEffect(() => {
    athSessionsNumRef.current = athSess.length;
  }, [athSess.length]);

  const getAthSessionsInCurrentDrill = () => data?.res?.athleteSessions
  .filter(( as ) => as.drills?.relatedDrills?.find(
    ( rd ) => currDrillRef.current === -1 || rd.index === currDrillRef.current,
  )) || [];

  const athSessionsInCurrentDrill = useMemo(
    getAthSessionsInCurrentDrill,
    [data?.res?.athleteSessions, currDrillRef.current],
  );

  const getAthSessionIDsInCurrentDrill = () => getAthSessionsInCurrentDrill().map(( as ) => as.id);

  const athSessionIDsInCurrentDrill = useMemo(
    getAthSessionIDsInCurrentDrill,
    [athSessionsInCurrentDrill],
  );

  const athInCurrentDrillRef = useRef<{
    athSessionsInCurrentDrill: AthleteSessionType[],
    athSessionIDsInCurrentDrill: string[],
  }>({
    athSessionIDsInCurrentDrill: [],
    athSessionsInCurrentDrill: [],
  });

  useEffect(() => {
    athInCurrentDrillRef.current = {
      athSessionIDsInCurrentDrill,
      athSessionsInCurrentDrill,
    };
  }, [athSessionsInCurrentDrill, athSessionIDsInCurrentDrill]);

  /* GROUND */
  const groundDetails = data?.res?.athleteSessions && data.res.athleteSessions.length > 0
    && data.res.athleteSessions[0] && data.res.athleteSessions[0].track?.ground;
  const isGPSGround = ground?.groundCoordsType !== 'LOCAL';
  const currentGround = !isGPSGround ? ground : ground || sessionGround;
  const teamColors = useMemo(() => getMarkersColor(currentGround?.groundSurfaceColor || DEFAULT_GROUND_COLOR), []);

  const drillTimes: DrillTimesType = useMemo(() => {
    const times = {};

    marks.forEach(( drill ) => {
      const {index, value} = drill;
      // @todo verificare null
      if (index) {
        if (!times[index]) {
          times[index] = {start: value, end: value};
        } else {
          times[index].start = Math.min(times[index].start, value);
          times[index].end = Math.max(times[index].end, value);
        }
      }
    });

    return Object.values(times);
  }, [marks]);

  const {
    data: dataGrounds,
  } = useQueryCached<ClubTypeRes, ClubTypeVars>(CLUB, {
    variables: {
      id: teamId,
    },
    ...CACHE_AND_NETWORK,
  });

  const groundSet = dataGrounds?.res?.club?.groundSet || [];

  const groundOptions = useMemo<OptionType[]>(() => groundSet?.map(( g ) => ({
    id: String(g.id),
    value: String(g.name),
    label: String(g.name),
  })), [groundSet]);

  const playersTeamsContextValue = useMemo(() => ({
    playersTeams, setPlayersTeams,
  }), [playersTeams, setPlayersTeams]);

  const handlePlayClick = ( forcePlay = false, dontSetLastRequestedTimestamp = false ) => {
    if (!dontSetLastRequestedTimestamp) {
      lastRequestedTimestampRef.current = cursorValue.cursor || null;
    }
    if (
      forcePlay || currentPlayerStatus === PLAYER_STATUS.STOP
    ) {
      // @todo va gestitia la situazione particolare dove ho dei player per il drill, ma non ci sono dati per i punti
      // nemmeno dopo i tentativi di caricamento
      fpsCtrlRef.current.start();
      setCurrentPlayerStatus(true);

    } else {
      fpsCtrlRef.current.pause();
      setCurrentPlayerStatus(false);
    }
  };

  const handleChange = ( _: ChangeEvent<{}> | null, time: number, background = false, isDrag = false ) => {
    if (endSessionTimestampRef?.current && time > endSessionTimestampRef.current) {
      return;
    }

    const currDrill = getCurrentDrill(time, drillTimes);
    let curTime = time;

    if (currDrill === -1 && drillTimes.length > 0) {
      const nextDrillIdx = drillTimes.findIndex((dt) => dt.start >= time)
      currDrillRef.current = nextDrillIdx + 1;
      curTime = drillTimes[nextDrillIdx].start;
    } else {
      currDrillRef.current = currDrill;
    }

    if (timestamps[curTime]) {
      // ho il time nei timestamp, posiziono idx
      setCurrentTime(curTime);
      lastRequestedTimestampRef.current = curTime;
    } else {
      // non ho time, ne cerco uno vicino
      const nearIdx = findNearTimestampIndex(curTime, timestamps);

      if (!background && nearIdx > -1) {
        // c'è uno vicino
        setCurrentTime(curTime);
        lastRequestedTimestampRef.current = curTime;

        if (!isDrag) {
          (async () => {
            await checkAndLoadBlock(curTime);
          })();
        }
      } else {
        // non c'è ne time ne uno vicino, mancano dati, richiedo nuovi dati
        if (!background) {
          setCurrentTime(curTime);
          lastRequestedTimestampRef.current = curTime;
        } else {
          logger('handleChange - 6');
        }

        if (!isDrag) {
          (async () => {
            await checkAndLoadBlock(curTime);
          })();
        } else {
          logger('handleChange - 9')
        }
      }
    }
  };

  const goToNextDrill = () => {
    const wasPlaying = currentPlayerStatus;

    if (wasPlaying) {
      handlePlayClick(false, true);
    }

    // currDrillRef.current è 1-based, drillTimes è zero based
    handleChange(null, drillTimes[currDrillRef.current].start);

    if (wasPlaying) {
      handlePlayClick(true, true);
    }
  };

  const goToPrevDrill = () => {
    const wasPlaying = currentPlayerStatus;
    if (wasPlaying) {
      handlePlayClick(false, true);
    }

    if (currDrillRef.current > 1) {
      currDrillRef.current -= 1;
    }

    // currDrillRef.current è 1-based, drillTimes è zero based
    handleChange(null, drillTimes[currDrillRef.current - 1].start);

    if (wasPlaying) {
      handlePlayClick(true, true);
    }
  };

  const handlePlayerClick = useCallback(( playerID: string ) => {
    if (!currentActivePlayer) {
      setCurrentActivePlayer(playerID);
    } else if (currentActivePlayer && currentActivePlayer === playerID) {
      setCurrentActivePlayer(null);
    } else {
      const existingLink = Array.from(playerLinks)
      .find(( link ) => link[0] === currentActivePlayer && link[1] === playerID
        || link[0] === playerID && link[1] === currentActivePlayer);

      if (!existingLink) {
        const newPlayerLinks = new Set(playerLinks);
        newPlayerLinks.add([currentActivePlayer, playerID]);
        setPlayerLinks(newPlayerLinks);
      }

      setCurrentActivePlayer(null);
    }
  }, [currentActivePlayer, playerLinks]);

  const handlePlayerEnter = ( playerID: string ) => {
    setCurrentHoverPlayer(playerID);
  };

  const handlePlayerLeave = () => {
    setCurrentHoverPlayer(null);
  };

  const playersDetails: PlayersDetails = useMemo(() => (detailsData?.res?.athleteSessions
    ? detailsData?.res?.athleteSessions.reduce(( acc, curr ) => {
      if (curr.athlete?.id) {
        acc[curr.id] = {
          id: curr.athlete?.id,
          name: curr.athlete?.name,
          number: curr.athlete?.playerSet.find(( ps ) => ps.team?.id && ps.team?.id === teamId)?.number || null,
          shortName: curr.athlete?.shortName,
        };
      }

      return acc;
    }, {})
    : {}), [detailsData?.res?.athleteSessions, teamId]);

  const handleRemoveLink = ( linkIdx: number ) => {
    // eslint-disable-next-line no-restricted-globals
    const newPlayerLinks = Array.from(playerLinks);
    newPlayerLinks.splice(linkIdx, 1);
    setPlayerLinks(new Set(newPlayerLinks));
  };

  const handleFullscreenClick = async () => {
    if (document.fullscreenElement) {
      await exitFullscreen();
    } else if (playerRef.current) {
      try {
        await enterFullscreen(playerRef.current);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const defaultPlayersColor = !isGPSGround
    ? ground?.groundSurfaceColor ? getPairedGroundColor(ground?.groundSurfaceColor) : PLAYER_MARKER_COLOR
    : sessionGround?.groundSurfaceColor
      ? getPairedGroundColor(sessionGround?.groundSurfaceColor)
      : PLAYER_MARKER_COLOR;

  const indexedMarks = useMemo(() => (marks || []).map(( m ) => ({
    ...m,
    idx: findNearTimestampIndex(m.value, timestamps),
  })), [marks]);

  useEffect(() => {
    logger('changed marks')
  }, [marks]);

  const isLoadingDataRef = useRef(false);
  const isLoadingData = currentPlayerStatus === PLAYER_STATUS.STOP && loadingPlayers.length > 0;

  useEffect(() => {
    isLoadingDataRef.current = isLoadingData;
  }, [isLoadingData]);

  const userUnits = useMemo(() => me.userunits, [me.userunits]);


  const playersExtraData = useMemo<PlayersExtraData>(() => enabledPlayers.reduce(( acc, pl ) => {
    acc[pl] = cursorValue.cursor && dataState && dataState[currentTimeSlot] && dataState[currentTimeSlot][pl] && dataState[currentTimeSlot][pl][cursorValue.cursor]
      ? {
        s: dataState[currentTimeSlot][pl][cursorValue.cursor].speed || null,
        hr: dataState[currentTimeSlot][pl][cursorValue.cursor].cardio || null,
        h: dataState[currentTimeSlot][pl][cursorValue.cursor].heading || null,
        x: dataState[currentTimeSlot][pl][cursorValue.cursor].x || null,
        y: dataState[currentTimeSlot][pl][cursorValue.cursor].y || null,
      }
      : {
        s: null, hr: null, h: null, x: null, y: null,
      };

    return acc;
  }, {}), [dataState, currentTimeSlot, enabledPlayers, cursorValue.cursor]);


  // gestione FpsCtrl
  const lastPlayerFPS = useRef(FPS); // @todo performance 0.3ms

  useEffect(() => {
    if (timestamps.length > 0 && !fpsCtrlRef.current || playerFPS !== lastPlayerFPS.current && lastRequestedTimestampRef.current) {
      if (fpsCtrlRef.current) {
        fpsCtrlRef.current.pause();
        fpsCtrlRef.current = null;
      }

      fpsCtrlRef.current = new FpsCtrl(
        playerFPS,
        ( timeData: { frame: number } ) => {
          const {frame} = timeData;


          setCurrentTime(( prevState ) => {
            if (!lastRequestedTimestampRef.current) {
              lastRequestedTimestampRef.current = prevState;
            }
            return (lastRequestedTimestampRef.current || prevState) + frame * (1 / playerFPS) * 1000
          });
        },
        playerSpeed,
      );
      lastPlayerFPS.current = playerFPS;
    }
  }, [timestamps, currentPlayerStatus, playerFPS, lastRequestedTimestampRef.current]);

  // aggiorno il drill corrente
  useEffect(() => {
    if (currentTime > 0 && drillTimes.length > 0) {
      const currDrill = getCurrentDrill(currentTime, drillTimes);

      if (currDrill === -1) {
        if (currentPlayerStatus === PLAYER_STATUS.PLAY) {
          goToNextDrill();
        }
      } else {
        currDrillRef.current = getCurrentDrill(currentTime, drillTimes);
      }
    }
  }, [currentTime, drillTimes]);

  const lastPlayerSpeedRef = useRef(1);

  // aggiornamento del moltiplicatore, stop? => modifica => play?
  useEffect(() => {
    if (fpsCtrlRef.current && lastPlayerSpeedRef.current !== playerSpeed) {
      currPlayerSpeedRef.current = playerSpeed;
      lastPlayerSpeedRef.current = playerSpeed;

      const wasPlaying = currentPlayerStatus;
      if (wasPlaying) {
        handlePlayClick(false, false);
      }

      fpsCtrlRef.current.setMultiplier(playerSpeed);

      if (wasPlaying) {
        handlePlayClick(true);
      }
    }
  }, [playerSpeed, currentPlayerStatus]);

  useEffect(() => {
    if (timestampsToAdd.length > 0) {
      if (
        currentPlayerStatus === PLAYER_STATUS.STOP
        && lastRequestedTimestampRef.current
      ) {
        setCurrentTime(lastRequestedTimestampRef.current);
      }
    }
  }, [timestampsToAdd, currentPlayerStatus]);

  useEffect(() => {
    if (
      !ground
      && groundOptions.length > 0
      && groundSet.length > 0
      && detailsData
      && data
    ) {
      const selectedGround = groundSet.find(( g ) => g.id === groundOptions[0].id);

      if (selectedGround) {
        setGround(selectedGround);
      }
    }
  }, [groundOptions, ground, groundSet, detailsData, data]);

  useEffect(() => {
    const keysEventListener = ( event: KeyboardEvent ) => keyboardEventHandler(event, [
      {key: 'Space', callbackFn: handlePlayClick},
      {key: 'ArrowLeft', callbackFn: () => fpsCtrlRef.current.stepBackward(1)},
      {key: 'ArrowLeft', callbackFn: () => fpsCtrlRef.current.stepBackward(10), shiftKey: true},
      {key: 'ArrowRight', callbackFn: () => fpsCtrlRef.current.stepForward(1)},
      {key: 'ArrowRight', callbackFn: () => fpsCtrlRef.current.stepForward(10), shiftKey: true},
    ]);

    document.addEventListener(
      'keydown',
      keysEventListener,
      false,
    );
    return () => {
      document.removeEventListener('keydown', keysEventListener);
    };
  }, [currentPlayerStatus, handlePlayClick]); // @todo check dependencies

  // @todo da aggiornare con il loader binario

  useEffect(() => {
    if (currentPlayerStatus) {
      // siamo in play
      const nextTime = currentTime + 30000;
      if (!isDecodingData && !dataState[getNextSlot(nextTime)]) {
        checkAndLoadBlock(nextTime);
      }
    }
  }, [currentTime, isDecodingData, dataState]);


  // @todo serve? Valutare per il recorder
  /*
  useEffect(() => {
    if (!isLoadingDataRef.current && prevPlayerIsLoadingRef.current) {
      if (window.playerIsReady) {
        console.log('[event] playerIsReady');
        window.playerIsReady({
          duration: (data
            ? currentDrill !== null && data
              ? data.res.drills.relatedDrills[currentDrill].totalTime.value
              : data.res.drills.wholeSession.totalTime.value
            : 0) * 1000,
        });
      }
    }

    prevPlayerIsLoadingRef.current = isLoadingDataRef.current;
  }, [isLoadingData]);
  */

  useEffect(() => {
    loadingPlayersRef.current = loadingPlayers.length;
  }, [loadingPlayers.length]);

  // @todo verifica map in produzione

  // gestisco stato player durante la riproduzione
  useEffect(() => {
    if (
      endSessionTimestampRef?.current
      && currentTime > endSessionTimestampRef.current
    ) {
      console.log('[idle preload] stop END_SESSION');
      fpsCtrlRef.current.pause();
      setCurrentPlayerStatus(false);
    }
  }, [endSessionTimestamp, currentTime, fpsCtrlRef.current]);


  const [isLoadingBinaryData, setIsLoadingBinaryData] = useState(false)
  const [playerDataStatus, setPlayerDataStatus] = useState<PlayerDataStatus>(PLAYER_DATA_STATUS.INITIAL)

  const decodeFirstBlock = async () => {
    const loadedData = await loadData(sessionId, timestamps[0]);
    setPlayerDataStatus(PLAYER_DATA_STATUS.DATA_DECODED);

    if (loadedData) {
      logger('Primo blocco decodificato', loadedData)
      setDataState(loadedData);
      setIsLoadingBinaryData(false);
    } else {
      console.error('Errore decodifica primo blocco dati')
    }
  }

  const checkAndLoadBlock = async ( timestamp: number ) => {
    const timeSlot = getSlotId(timestamp);
    logger('checkAndLoadBlock', dataState, timeSlot)
    if (!dataState || !dataState[timeSlot]) {
      setIsDecodingData(true);
      logger('Decodifica blocco', timeSlot)
      const newLoadedData = await loadData(sessionId, getSlotStartTimestamp(timestamp));

      if (newLoadedData) {
        // tengo solo la chiave corrente, la precedente e le tre successive
        const timeSlotsToRemove = filterNeighbors(
          [
            ...Object.keys(dataState),
            ...[String(timeSlot)],
          ],
          String(timeSlot),
        );

        let newDataState: null | { [k: string]: any } = {
          // rimuovo i blocchi non necessari
          ...Object.keys(dataState).reduce(( acc, curr ) => {
            if (!timeSlotsToRemove.includes(curr)) {
              acc[curr] = dataState[curr];
            }

            return acc;
          }, {}),
          // aggiungo il nuovo blocco
          [timeSlot]: newLoadedData[timeSlot]
        }

        setDataState(newDataState);
        setIsDecodingData(false);
        newDataState = null;
      }
    }
  }

  const cleanupMemory = useCallback(() => {
    // Pulisce i dati delle serie non più necessari
    setSeries(( prev ) => {
      const cleanedSeries = {};
      // Mantiene solo i dati dell'ultimo slot per ogni player
      Object.keys(prev).forEach(( playerId ) => {
        if (enabledPlayers.includes(playerId)) {
          cleanedSeries[playerId] = prev[playerId];
        }
      });
      return cleanedSeries;
    });

    // Pulisce il dataState mantenendo solo gli slot recenti
    setDataState(( prev ) => {
      if (!prev) return prev;

      const currentSlot = getSlotId(currentTime);
      const cleanedState = {};

      // Mantiene solo gli slot vicini a quello corrente
      Object.keys(prev).forEach(slotId => {
        const slotNum = parseInt(slotId);
        if (Math.abs(slotNum - currentSlot) <= 2) {
          cleanedState[slotId] = prev[slotId];
        }
      });

      return cleanedState;
    });

    // Forza garbage collection se disponibile
    if (global.gc) {
      global.gc();
    }
  }, [currentTime, enabledPlayers]);

  // Esegui cleanup periodico
  useEffect(() => {
    const cleanupInterval = setInterval(cleanupMemory, 30000); // ogni 30 secondi

    return () => clearInterval(cleanupInterval);
  }, [cleanupMemory]);

  // Cleanup quando il componente viene smontato
  useEffect(() => {
    return () => {
      cleanupMemory();
      // Pulisce il manager della sessione
      if (dbManager) {
        dbManager.clearCache();
      }
    };
  }, []);

  const tryLoadingData = async () => {
    const token = localStorage.getItem('exelio_token');

    if (!token) {
      console.log('Anomalia - No Token') // @todo logout?
      return;
    }

    const resp = await loadSessionData(sessionId, token);

    if (resp.status === 202) {
      logger('tryLoadingData - waiting for data');
      setIsLoadingBinaryData(true);
      setPlayerIsLoading(false);
      await delay(10000);
      if (!isLoadingBinaryData) {
        await tryLoadingData();
      }
    } else {
      logger('tryLoadingData - done', resp)
      setLoadingPlayers(resp.athSessions.map((athSessUrl) => athSessUrl.athleteSessionId));

      setPlayerIsLoading(false);

      if (await packData(
        resp.athSessions,
        sessionId,
        (id) => setLoadingPlayers((prevState) => prevState.filter(k => k !== id)),
        () => setLoadingPlayers([])
      )) {
        // dati binari caricati, avvio decodifica del primo slot
        logger('Dati binari scaricati', timestamps);
        if (timestamps.length === 0) {
          logger('MANCANO I TIMESTAMPS - 1')
        }
      } else {
        // download dei dati binari fallita
        console.error('Errore download dati sessione')
      }
    }
  }

  useEffect(() => {
    const promise = async () => {
      if (timestamps.length === 0) {
        setPlayerDataStatus(PLAYER_DATA_STATUS.TIMESTAMPS_LOADING);
        await loadTimestamps();
        setPlayerDataStatus(PLAYER_DATA_STATUS.TIMESTAMPS_LOADED);
      }
    }

    if (
      playerDataStatus === PLAYER_DATA_STATUS.INITIAL
      && timestamps.length === 0
    ) {
      promise();
    }
  }, [timestamps, sessionId, playerDataStatus]);

  useEffect(() => {
    const promise = async () => {
      if (!(await isSessionDataAvailable(sessionId))) {
        setPlayerDataStatus(PLAYER_DATA_STATUS.DATA_LOADING);
        await tryLoadingData();
        setPlayerDataStatus(PLAYER_DATA_STATUS.DATA_LOADED);
      }

      setPlayerIsLoading(false);
      setPlayerDataStatus(PLAYER_DATA_STATUS.DATA_DECODING);
      await decodeFirstBlock();
      setPlayerDataStatus(PLAYER_DATA_STATUS.READY);
    }

    if (
      playerDataStatus === PLAYER_DATA_STATUS.TIMESTAMPS_LOADED
    ) {
      promise();
    }
  }, [sessionId, playerDataStatus]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', () => {
      setPlayerIsFullscreen(!!document.fullscreenElement);
    });

    return () => {
      document.removeEventListener('fullscreenchange');
    }

  }, []);

  const isGroundLoading = playerDataStatus !== PLAYER_DATA_STATUS.READY;
  const isPlayerEnabled = !isGroundLoading && !isDecodingData;
  const isLoading = isGroundLoading || isLoadingBinaryData || !ground;
  const fullscreenMode = isFullscreen || playerIsFullscreen;

  return (
    <ErrorGuard>
      <TourProvider steps={steps} scrollSmooth>
        <div className="tracks-player" ref={playerRef}>
          {
            playerIsLoading && <Loader />
          }

          {
            !playerIsLoading && !isLoadingBinaryData
            && (
              <>
                {
                  !fullscreenMode
                  && (
                    <WebPlayerHeader
                      ground={ground}
                      groundOptions={groundOptions}
                      groundSet={groundSet}
                      jerseyOrNumber={jerseyOrNumber}
                      setGround={setGround}
                      setJerseyOrNumber={setJerseyOrNumber}
                      setTrailsEnabled={setTrailsEnabled}
                      trailsEnabled={trailsEnabled}
                      isSessionDataReady={!!data}
                    />
                  )
                }

                <Row className="tracks-player-container">
                  <PlayersTeamsContext.Provider value={playersTeamsContextValue}>
                    <Column xs={fullscreenMode ? 12 : 8} className="tracks-player-column">
                      <Box className="player__wrapper__outer" p={fullscreenMode ? 0 : 2} style={{paddingLeft: fullscreenMode ? '0' : '10px', aspectRatio: 16/9}}>
                        <CursorContext.Provider value={cursorValue}>
                          <div className="player__wrapper__inner" style={{aspectRatio: fullscreenMode ? 'initial' : '16/9', width: '100%', height: '100%', position: 'relative'}}>
                            {
                              currentGround
                              && (
                                <Ground
                                  currentActivePlayer={currentActivePlayer}
                                  defaultPlayersColor={defaultPlayersColor}
                                  enabledPlayers={enabledPlayers}
                                  fullscreenMode={fullscreenMode}
                                  ground={currentGround}
                                  handlePlayerClick={handlePlayerClick}
                                  handlePlayerEnter={handlePlayerEnter}
                                  handlePlayerLeave={handlePlayerLeave}
                                  handleRemoveLink={handleRemoveLink}
                                  hoverPlayer={currentHoverPlayer}
                                  infoPlayers={infoPlayers}
                                  isDecodingData={isDecodingData}
                                  isLoadingData={isLoading}
                                  loadingPlayers={loadingPlayers}
                                  jerseyOrNumber={jerseyOrNumber}
                                  playerLinks={playerLinks}
                                  playersColors={playersColors}
                                  playersDetails={playersDetails}
                                  series={dataState && dataState[currentTimeSlot] || {}}
                                  showVertices={showVertices}
                                  teamColors={teamColors}
                                  trailsEnabled={trailsEnabled}
                                  userUnits={userUnits}
                                />
                              )
                            }
                          </div>

                          <PlayerControls
                            currDrillRef={currDrillRef}
                            currentDrill={currentDrill}
                            currentTime={currentTime}
                            drillTimes={drillTimes}
                            enabled={isPlayerEnabled} // !isLoadingForeground
                            endTimestamp={data?.res?.endTimestamp}
                            goToNextDrill={goToNextDrill}
                            goToPrevDrill={goToPrevDrill}
                            handleChange={handleChange}
                            handleFullscreenClick={handleFullscreenClick}
                            handlePlayClick={() => handlePlayClick()}
                            intervals={intervals}
                            loadingInterval={loadingInterval}
                            marks={indexedMarks}
                            playerSpeed={playerSpeed}
                            playerStatus={currentPlayerStatus ? 1 : 0} // @todo usare stati boolean
                            setPlayerSpeed={setPlayerSpeed}
                            startTimestamp={data?.res?.startTimestamp}
                            timeFormat={timeFormat}
                            timestamps={timestamps}
                            valueLabelFormat={valueLabelFormat}
                          />

                        </CursorContext.Provider>
                      </Box>
                    </Column>

                    {
                      !fullscreenMode
                      && (
                        <Column xs={4}>
                          <PlayersTable
                            activeAthSessions={athSessionIDsInCurrentDrill}
                            athleteSessions={detailsData?.res?.athleteSessions || []}
                            enabledPlayers={enabledPlayers}
                            groundSurfaceColor={ground?.groundSurfaceColor || DEFAULT_GROUND_COLOR}
                            infoPlayers={infoPlayers}
                            jerseyOrNumber={jerseyOrNumber}
                            loadingPlayers={!isPlayerEnabled ? loadingPlayers : []}
                            playersColors={playersColors}
                            playersDetails={playersDetails}
                            playersExtraData={playersExtraData}
                            setEnabledPlayers={setEnabledPlayers}
                            setInfoPlayers={setInfoPlayers}
                            setPlayersColors={setPlayersColors}
                            status={status}
                            teamColors={teamColors}
                            userUnits={userUnits}
                          />

                          <StaffOnlyWrapper>
                            <TourGuide/>
                          </StaffOnlyWrapper>
                        </Column>
                      )
                    }

                  </PlayersTeamsContext.Provider>
                </Row>
              </>
            )
          }

          {
            (!playerIsLoading && isLoadingBinaryData) && (
              <InlineMessage
                iconName="info"
                title={t('webPlayer.isLoadingData', 'Player positional data is loading')}
                text={
                  t('webPlayer.isLoadingData.pageMessage', `Please wait a few minutes. You will receive a notification when data is ready.`)
                }
              />
            )
          }

        </div>
      </TourProvider>
    </ErrorGuard>
  );
};

export default withMe(WebPlayer);
